import { LayoutApp } from "@/components/layoutApp";
import { Progress } from "@/components/ui/progress";
import { RecuperacaoSenhaService } from "@/services/auth/recuperarSenha";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from "react";
import type { Control } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

// Funções de validação
function validateCPF(cpf: string): boolean {
  cpf = cpf.replace(/[^\d]/g, "");
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }

  let remainder = sum % 11;
  const checkDigit1 = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cpf.charAt(9)) !== checkDigit1) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }

  remainder = sum % 11;
  const checkDigit2 = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(cpf.charAt(10)) === checkDigit2;
}

function validateCNPJ(cnpj: string): boolean {
  cnpj = cnpj.replace(/[^\d]/g, "");
  
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpj)) return false;
  
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnpj.charAt(i)) * weights1[i];
  }
  
  let remainder = sum % 11;
  const checkDigit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(cnpj.charAt(12)) !== checkDigit1) return false;
  
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cnpj.charAt(i)) * weights2[i];
  }
  
  remainder = sum % 11;
  const checkDigit2 = remainder < 2 ? 0 : 11 - remainder;
  
  return parseInt(cnpj.charAt(13)) === checkDigit2;
}

function validateCPForCNPJ(value: string): boolean {
  const cleanValue = value.replace(/[^\d]/g, "");
  
  if (cleanValue.length === 11) {
    return validateCPF(cleanValue);
  } else if (cleanValue.length === 14) {
    return validateCNPJ(cleanValue);
  }
  
  return false;
}

// Define validation schemas
const documentoSchema = z.object({
  documento: z
    .string()
    .min(11, "Documento precisa ter pelo menos 11 dígitos")
    .max(18, "Documento inválido")
    .refine((val) => validateCPForCNPJ(val), "Documento inválido"),
});

const codigoSchema = z.object({
  codigo: z
    .string()
    .length(6, "O código deve ter 6 dígitos")
    .regex(/^\d+$/, "O código deve conter apenas números"),
});

const novaSenhaSchema = z
  .object({
    novaSenha: z.string().min(6, "A senha precisa ter no mínimo 6 caracteres"),
    confirmarSenha: z.string().min(6, "Confirmação de senha inválida"),
  })
  .refine((data) => data.novaSenha === data.confirmarSenha, {
    message: "Senhas não correspondem",
    path: ["confirmarSenha"],
  });

interface FormInputProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  placeholder: string;
  type?: string;
  inputMode?: 
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  onBlur?: () => void;
  icon?: React.ReactNode;
}

type DocumentoFormData = z.infer<typeof documentoSchema>;
type CodigoFormData = z.infer<typeof codigoSchema>;
type NovaSenhaFormData = z.infer<typeof novaSenhaSchema>;

export function ForgotPassword() {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [recoveryId, setRecoveryId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [tpmToken, setTpmToken] = useState<string | null>(null);

  const navigate = useNavigate();
  const recuperacaoService = new RecuperacaoSenhaService();

  useEffect(() => {
    setCurrentStep(1);
    setRecoveryId(null);
    setEmail(null);
    setTpmToken(null);
  }, []);

  const documentoForm = useForm<DocumentoFormData>({
    resolver: zodResolver(documentoSchema),
    defaultValues: {
      documento: "",
    },
  });

  const codigoForm = useForm<CodigoFormData>({
    resolver: zodResolver(codigoSchema),
    defaultValues: {
      codigo: "",
    },
  });

  const novaSenhaForm = useForm<NovaSenhaFormData>({
    resolver: zodResolver(novaSenhaSchema),
    defaultValues: {
      novaSenha: "",
      confirmarSenha: "",
    },
  });

  const solicitarRecuperacaoMutation = useMutation({
    mutationFn: (documento: string) => recuperacaoService.solicitarRecuperacao(documento),
    onSuccess: (data) => {
      if (data.success) {
        setRecoveryId(data.recoveryId);
        setEmail(data.email);
        setCurrentStep(2);
        toast.success("Código enviado!", {
          description: "Um código de verificação foi enviado para seu e-mail cadastrado."
        });
      }
    },
    onError: (error) => {
      console.error("Erro ao solicitar recuperação:", error);
      toast.error("Erro na recuperação", {
        description: "Não foi possível solicitar a recuperação de senha. Verifique o documento informado."
      });
    },
  });

  const verificarCodigoMutation = useMutation({
    mutationFn: ({ recoveryId, codigo }: { recoveryId: string; codigo: string }) => 
      recuperacaoService.verificarCodigo(recoveryId, codigo),
    onSuccess: (data) => {
      if (data.success) {
        setCurrentStep(3);
        setTpmToken(data.token);
      } else {
        toast.error("Código inválido", {
          description: "O código informado não é válido."
        });
      }
    },
    onError: (error) => {
      console.error("Erro ao verificar código:", error);
      toast.error("Erro na verificação", {
        description: "Ocorreu um erro ao verificar o código. Tente novamente."
      });
    },
  });

  const definirNovaSenhaMutation = useMutation({
    mutationFn: ({ novaSenha }: { recoveryId: string; novaSenha: string }) => 
      recuperacaoService.definirNovaSenha(novaSenha, tpmToken!),
    onSuccess: () => {
      toast.success("Senha alterada com sucesso!", {
        description: "Sua nova senha foi definida. Você pode fazer login agora."
      });
      setCurrentStep(1);
      setRecoveryId(null);
      setTpmToken(null);
      setEmail(null);
      
      documentoForm.reset();
      codigoForm.reset();
      novaSenhaForm.reset();
      navigate('/');
    },
    onError: (error) => {
      console.error("Erro ao definir nova senha:", error);
      toast.error("Erro ao definir senha", {
        description: "Ocorreu um erro ao definir sua nova senha. Tente novamente."
      });
    },
  });

  const handleSolicitarRecuperacao = async (): Promise<void> => {
    const isValid = await documentoForm.trigger("documento");

    if (!isValid) {
      toast.error("Documento inválido", {
        description: "Verifique se o CPF ou CNPJ foi digitado corretamente."
      });
      return;
    }

    solicitarRecuperacaoMutation.mutate(documentoForm.getValues("documento"));
  };

  const handleVerificarCodigo = async (): Promise<void> => {
    const isValid = await codigoForm.trigger("codigo");

    if (!isValid || !recoveryId) {
      toast.error("Código inválido", {
        description: "Verifique se o código de 6 dígitos foi digitado corretamente."
      });
      return;
    }

    verificarCodigoMutation.mutate({
      recoveryId,
      codigo: codigoForm.getValues("codigo"),
    });
  };

  const handleDefinirNovaSenha = async (
    data: NovaSenhaFormData
  ): Promise<void> => {
    if (!recoveryId) {
      toast.error("Sessão expirada", {
        description: "Por favor, comece o processo novamente."
      });
      setCurrentStep(1);
      return;
    }

    definirNovaSenhaMutation.mutate({
      recoveryId,
      novaSenha: data.novaSenha,
    });
  };

  const FormInput: React.FC<FormInputProps> = ({
    control,
    name,
    placeholder,
    type = "text",
    inputMode = "text",
    onBlur,
    icon,
  }) => (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur: fieldOnBlur, value },
        fieldState: { error },
      }) => (
        <div className="mb-3">
          <div
            className={`flex items-center w-full bg-white rounded-full h-16 px-3 border ${
              error ? "border-red-500" : "border-gray-300"
            } transition-colors duration-200 focus-within:border-blue-500`}
          >
            {icon && <div className="mr-3 text-gray-400">{icon}</div>}
            <input
              className="flex-1 text-gray-800 text-sm bg-transparent outline-none placeholder-gray-400"
              placeholder={placeholder}
              onChange={onChange}
              onBlur={() => {
                fieldOnBlur();
                if (onBlur) onBlur();
              }}
              value={value}
              type={type}
              inputMode={inputMode}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
            />
          </div>
          {error && (
            <p className="text-red-500 text-xs ml-3 mt-1">
              {error.message}
            </p>
          )}
        </div>
      )}
    />
  );

  const ActionButton: React.FC<{
    title: string;
    onPress: () => void;
    isLoading?: boolean;
    backgroundColor?: string;
    textColor?: string;
    className?: string;
  }> = ({
    title,
    onPress,
    isLoading = false,
    backgroundColor = "#1d71b8",
    textColor = "white",
    className = "w-full",
  }) => (
    <button
      className={`${className} rounded-full h-16 flex items-center justify-center transition-all duration-200  font-medium text-sm ${
        isLoading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90 active:scale-95"
      }`}
      style={{ backgroundColor }}
      onClick={onPress}
      disabled={isLoading}
    >
      {isLoading ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : (
        <span style={{ color: textColor }}>{title}</span>
      )}
    </button>
  );

  const ProgressBar: React.FC = () => (
    <div className="mb-4 mt-2">
      <div className="flex justify-between mb-1">
        <span className="text-xs text-gray-500 font-medium">
          Passo {currentStep} de 3
        </span>
        <span className="text-xs text-blue-500 font-medium">
          {Math.round((currentStep / 3) * 100)}%
        </span>
      </div>
      <Progress value={(currentStep / 3) * 100} className="h-1" />
    </div>
  );

  const StepIndicator: React.FC = () => (
    <div className="flex justify-center mb-4">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div
            className={`w-6 h-6 rounded-full ${
              currentStep >= step ? "bg-blue-500" : "bg-gray-300"
            } flex items-center justify-center transition-colors duration-300`}
          >
            <span className="text-white font-bold text-xs">{step}</span>
          </div>
          {step < 3 && (
            <div
              className={`h-0.5 w-6 transition-colors duration-300 ${
                currentStep > step ? "bg-blue-500" : "bg-gray-300"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <LayoutApp header={{ title: "Recuperação de Senha", backButton: true, onBack: () => navigate("/") }}>
      <div className="bg-gray-50 py-2 min-h-full">
        <div className="max-w-sm mx-auto px-4">
          <h1 className="text-xl font-bold text-center text-blue-600 mb-4">
            Recuperação de Senha
          </h1>

          <StepIndicator />
          <ProgressBar />

          {currentStep === 1 && (
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <h2 className="text-lg font-semibold mb-2 text-gray-800">
                Verificação de Documento
              </h2>
              <p className="text-gray-500 text-sm mb-4">
                Digite seu CPF ou CNPJ para recuperar sua senha
              </p>

              <FormInput
                control={documentoForm.control}
                name="documento"
                placeholder="CPF ou CNPJ (apenas números)"
                inputMode="numeric"
              />

              <ActionButton
                title="Recuperar Senha"
                onPress={handleSolicitarRecuperacao}
                isLoading={solicitarRecuperacaoMutation.isPending}
                backgroundColor="#1d71b8"
              />
            </div>
          )}

          {currentStep === 2 && (
            <div className="bg-white p-6 rounded-3xl shadow-lg">
              <h2 className="text-xl font-semibold mb-2 text-gray-800">
                Código de Verificação
              </h2>
              <p className="text-gray-500 mb-6">
                Digite o código de 6 dígitos enviado para seu e-mail
                {email && ` (${email})`}
              </p>

              <FormInput
                control={codigoForm.control}
                name="codigo"
                placeholder="Código de 6 dígitos"
                inputMode="numeric"
              />

              <div className="flex justify-between mt-4 gap-3">
                <button
                  className="w-1/2 bg-gray-200 rounded-full h-16 flex items-center justify-center transition-all duration-200 hover:bg-gray-300 active:scale-95"
                  onClick={() => {
                    setCurrentStep(1);
                    setRecoveryId(null);
                    codigoForm.reset();
                  }}
                >
                  <span className="text-gray-700 font-medium text-sm">
                    Voltar
                  </span>
                </button>

                <ActionButton
                  title="Verificar"
                  onPress={handleVerificarCodigo}
                  isLoading={verificarCodigoMutation.isPending}
                  backgroundColor="#1d71b8"
                  className="w-1/2"
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="bg-white p-6 rounded-3xl shadow-lg">
              <h2 className="text-xl font-semibold mb-2 text-gray-800">
                Nova Senha
              </h2>
              <p className="text-gray-500 mb-6">
                Defina sua nova senha
              </p>

              <FormInput
                control={novaSenhaForm.control}
                name="novaSenha"
                placeholder="Nova senha"
                type="password"
              />

              <FormInput
                control={novaSenhaForm.control}
                name="confirmarSenha"
                placeholder="Confirme a nova senha"
                type="password"
              />

              <div className="flex justify-between mt-4 gap-3">
                <button
                  className="w-1/2 bg-gray-200 rounded-full h-16 flex items-center justify-center transition-all duration-200 hover:bg-gray-300 active:scale-95"
                  onClick={() => {
                    setCurrentStep(1);
                    novaSenhaForm.reset();
                    codigoForm.reset();
                  }}
                >
                  <span className="text-gray-700 font-medium text-sm">
                    Voltar
                  </span>
                </button>

                <ActionButton
                  title="Confirmar"
                  onPress={novaSenhaForm.handleSubmit(handleDefinirNovaSenha)}
                  isLoading={definirNovaSenhaMutation.isPending}
                  backgroundColor="#1d71b8"
                  className="w-1/2"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutApp>
  );
}